"use client";

import { cn } from "@/lib/utils";
import type { BasketballTemplateData } from "../basketball-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type BasketballScorecardProps = {
  data: BasketballTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function BasketballScorecard({
  data,
  participants: _participants,
  variant = "feed",
}: BasketballScorecardProps) {
  if (!data) return null;

  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const { yourPoints, opponentPoints } = data.matchScore;
  const yourWon = yourPoints > opponentPoints;
  const opponentWon = opponentPoints > yourPoints;
  const isDraw = yourPoints === opponentPoints;

  const scoreTextSize = variant === "feed" ? "text-4xl" : "text-3xl";

  const content = (
    <>
      <div className="mb-3 flex items-center justify-center gap-6">
        <div className="text-center">
          <div
            className={cn(
              "font-bold",
              scoreTextSize,
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {yourPoints}
          </div>
          <div className="text-xs text-text-secondary">Team 1</div>
        </div>
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <div className="text-center">
          <div
            className={cn(
              "font-bold",
              scoreTextSize,
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {opponentPoints}
          </div>
          <div className="text-xs text-text-secondary">Team 2</div>
        </div>
      </div>

      {/* Result badge */}
      <div className="mb-4 flex justify-center">
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            yourWon && "bg-brand/20 text-brand",
            opponentWon && "bg-error/20 text-error",
            isDraw && "bg-text-secondary/20 text-text-secondary",
          )}
        >
          {yourWon ? "🏆 Team 1 Wins" : opponentWon ? "Team 2 Wins" : "Draw"}
        </span>
      </div>

      {data.periods && data.periods.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 border-t border-border pt-3">
          {data.periods.map((period, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: period entries are positional and never reorder
              key={`period-${i}-${period.label}-${period.yourScore}-${period.opponentScore}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {period.label} {period.yourScore}–{period.opponentScore}
            </span>
          ))}
        </div>
      )}
    </>
  );

  if (variant === "feed") return content;
  return <ScorecardShell variant={variant}>{content}</ScorecardShell>;
}
